import { EmptyState, FlexColumn, MaterialIcon, useStyles } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { LabelLarge } from 'baseui/typography';
import { memo, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CAPTURE_DURATION } from '../../../constants/streaming';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { deleteDeviceConnection, postStreamingCommand } from '../../../services/streamingService';
import { MinimalApiAsset } from '../../../types/asset';
import { ChannelId } from '../../../types/gptTypes';
import { CaptureDuration, FlespiItem, MediaStream, StreamingCommand, StreamType } from '../../../types/streaming';
import { fitDimensions } from '../../../utils/app';
import HlsPlayer from './HlsPlayer';

type PlaybackPlayerProps = Readonly<{
  asset: MinimalApiAsset;
  channel: ChannelId;
  duration: CaptureDuration;
  playerRef: React.RefObject<HTMLVideoElement>;
  videoLayer?: React.ReactNode;
  stuck: boolean;
  canLoad?: boolean;
  onBadRequest: () => void;
  onRefresh: () => void;
  onLoadEnd?: () => void;
  streamType?: StreamType;
  startDate: number;
  poster?: string;
  withAudio?: boolean;
  muted?: boolean;
  maxWidth: number;
}>;

function PlaybackPlayer({
  asset,
  channel,
  playerRef,
  stuck,
  onBadRequest,
  onRefresh,
  videoLayer,
  onLoadEnd,
  startDate,
  canLoad,
  streamType,
  duration,
  poster,
  withAudio = false,
  muted = false,
  maxWidth,
}: PlaybackPlayerProps) {
  const { css, theme, concatenateClasses } = useStyles();
  const [internalError, setInternalError] = useState(false);
  const persistVolume = useRef(muted ? 0 : 0.5);
  const [flespiItem, setFlespiItem] = useState<FlespiItem>();
  const playbackUrl = flespiItem?.response?.url;

  useEffect(() => {
    const volumeTimeout = setTimeout(() => {
      if (playerRef.current) {
        playerRef.current.volume = persistVolume.current;
      }
    }, 0);
    return () => {
      clearTimeout(volumeTimeout);
    };
  }, [playerRef]);

  // Reset internal error when context changes
  useUpdateEffect(() => {
    setInternalError(false);
    playerRef.current?.pause();
  }, [channel, stuck, asset.assetId, asset.clientId, asset.subClientId]);

  const handleError = useCallback((event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const error = event.currentTarget?.error;
    if (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      onBadRequest();
    } else if (error) {
      setInternalError(true);
      setFlespiItem(undefined);
    }
  }, []);

  useEffect(() => {
    if (!canLoad) {
      return;
    }
    if (stuck) {
      setFlespiItem(undefined);
      return;
    }
    const loadUrl = async () => {
      if (!startDate) return;
      const { streaming, error } = await postStreamingCommand({
        assetId: asset.assetId,
        command: StreamingCommand.playbackVideo,
        channelId: channel,
        streamType: StreamType.main, // TODO: check if sub can be used.
        clientId: asset.clientId,
        subClientId: asset.subClientId,
        fromTime: Math.trunc(startDate / 1000),
        mediaStream: MediaStream.hls,
        duration: CAPTURE_DURATION[duration],
        audio: withAudio,
      });
      const item = streaming ? streaming.flespiResponse.result[0] : undefined;
      if (error || item?.executed === false) {
        onBadRequest();
      } else {
        setFlespiItem(item);
      }
    };

    loadUrl();
  }, [asset.clientId, asset.subClientId, asset.assetId, channel, startDate, streamType, duration, canLoad, stuck]);

  useEffect(() => {
    return () => {
      const closeConnection = async (connectionId: string) => {
        await deleteDeviceConnection({
          assetId: asset.assetId,
          connectionId,
          clientId: asset.clientId,
          subClientId: asset.subClientId,
        });
      };
      const connectionId = flespiItem?.response.connection_id?.toString();
      if (connectionId) {
        closeConnection(connectionId);
      }
    };
  }, [flespiItem?.response.connection_id]);

  const PlayerContent = (
    <>
      {stuck && (
        <FlexColumn
          centered
          gap={theme.sizing.scale200}
        >
          <MaterialIcon
            name="videocam_off"
            size={theme.sizing.scale800}
            color={theme.colors.contentInversePrimary}
          />
          <LabelLarge color={theme.colors.contentInversePrimary}>Desconectado</LabelLarge>
          <Button
            kind="secondary"
            size="mini"
            colors={{ color: 'white', backgroundColor: theme.colors.gray100Dark }}
            startEnhancer={
              <MaterialIcon
                name="sync"
                size={theme.sizing.scale600}
                color={theme.colors.contentInversePrimary}
              />
            }
            onClick={onRefresh}
          >
            Refrescar
          </Button>
        </FlexColumn>
      )}
      {!stuck && (
        <>
          {internalError && (
            <EmptyState
              style={{ background: theme.colors.backgroundPrimary, paddingBottom: theme.sizing.scale300 }}
              description="Ocurrió un error."
              action={
                <Button
                  shape="default"
                  kind="secondary"
                  size="mini"
                  onClick={() => setInternalError(false)}
                  startEnhancer={
                    <MaterialIcon
                      name="refresh"
                      size={theme.sizing.scale600}
                    />
                  }
                >
                  Recargar
                </Button>
              }
            />
          )}
          {!internalError && !playbackUrl && <LoadingSpinner $size="medium" />}
          {!internalError && canLoad && playbackUrl && (
            <HlsPlayer
              className="player"
              src={playbackUrl}
              autoPlay={true}
              controls
              playerRef={playerRef}
              onVolumeChange={(e) => (persistVolume.current = e.currentTarget.volume)}
              onError={handleError}
              style={{
                height: '100%',
                width: '100%',
                userSelect: 'none',
                cursor: 'default',
              }}
              onBadRequest={onBadRequest}
              onLoadEnd={onLoadEnd}
              preload="none"
              muted={muted}
              poster={poster}
              initialVolume={persistVolume.current}
            />
          )}
        </>
      )}
      {videoLayer}
    </>
  );
  const { width: scaledWidth, height: scaledHeight } = fitDimensions({
    width: flespiItem?.response.width,
    height: flespiItem?.response.height,
    maxWidth,
  });

  return (
    <FlexColumn
      centered
      classNames={concatenateClasses(
        css({
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          backgroundColor: theme.colors.backgroundInversePrimary,
          position: 'relative',
          borderRadius: theme.borders.radius300,
          overflow: 'hidden',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundImage: `url('${poster}')`,
          backgroundPosition: 'center',
        }),
      )}
    >
      {PlayerContent}
    </FlexColumn>
  );
}

export default memo(PlaybackPlayer);
